import * as msal from "@azure/msal-browser";
import type { BrowserSystemOptions, CacheOptions } from "@azure/msal-browser";
import type { AuthError } from "@azure/msal-common";
import { jwtDecode } from "jwt-decode";

import type { Auth, DataObject, iMSAL, Options, Request } from "./types";

const REDIRECT_URI_STORE_KEY = "vue-msal-redirect-uri";
let monitorExpiresOn: Date = new Date(0);

export class MSAL implements iMSAL {
  private msalLibrary: any;
  private tokenExpirationTimers: { [key: string]: undefined | number } = {};
  public data: DataObject = {
    accessToken: "",
    idToken: "",
    user: { name: "", email: "" },
    account: {
      homeAccountId: "",
      username: "",
      name: "",
      idTokenClaims: {},
      environment: "",
      tenantId: "",
      localAccountId: ""
    },
    roles: []
  };

  // Config object to be passed to Msal on creation.
  // For a full list of msal.js configuration parameters,
  // visit https://azuread.github.io/microsoft-authentication-library-for-js/docs/msal/modules/_authenticationparameters_.html
  private auth: Auth = {
    clientId: "",
    authority: "",
    redirectUri: "",
    postLogoutRedirectUri: "",
    autoRefreshToken: true,
    onAuthentication: (_ctx, _error, _response) => {},
    onToken: (_ctx, _error, _response) => {},
    beforeSignOut: (_ctx) => {},
    onLogError: (error) => {
      console.error("MSAL Error: ", error);
    }
  };

  private cache: CacheOptions = {
    cacheLocation: "sessionStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: false // Set this to 'true' if you are having issues on IE11 or Edge
  };
  // Add here scopes for id token to be used at MS Identity Platform endpoints.
  private loginRequest: Request = {
    scopes: ["openid", "profile", "User.Read"]
  };

  // Add here scopes for access token to be used at MS Graph API endpoints.
  private tokenRequest: Request = {
    scopes: ["User.Read"]
  };

  private system: BrowserSystemOptions = {
    loggerOptions: {}
  };

  constructor(options: Options) {
    if (!options.auth.clientId) {
      throw new Error("auth.clientId is required");
    }

    this.auth = Object.assign(this.auth, options.auth);
    this.cache = Object.assign(this.cache, options.cache);
    this.system = Object.assign(this.system, options.system);
    this.loginRequest = Object.assign(this.loginRequest, options.loginRequest);
    this.tokenRequest = Object.assign(this.tokenRequest, options.tokenRequest);

    const config: msal.Configuration = {
      auth: this.auth,
      cache: this.cache,
      system: this.system
    };

    this.msalLibrary = new msal.PublicClientApplication(config);
  }

  async init() {
    await this.msalLibrary.initialize();

    // Register Callbacks for redirect flow
    try {
      const tokenResponse = await this.msalLibrary.handleRedirectPromise();

      if (tokenResponse !== null) {
        this.data.user.name = tokenResponse.account?.name || "";
        this.data.user.email = tokenResponse.account?.username || "";
        this.data.accessToken = tokenResponse.accessToken;
        this.data.idToken = tokenResponse.idToken;
        this.data.account = tokenResponse.account;
        this.data.roles = (tokenResponse.idTokenClaims as any)["roles"] || [];

        this.auth.onAuthentication(this, null, tokenResponse);
      }

      this.selectAccount();
      await this.acquireToken();

      const redirectUri = window.localStorage.getItem(REDIRECT_URI_STORE_KEY);

      if (redirectUri) {
        window.location.pathname = redirectUri;
        window.localStorage.removeItem(REDIRECT_URI_STORE_KEY);
      }
    } catch (error: any) {
      this.auth.onLogError("handleRedirectPromise has thrown: " + error);
      this.auth.onAuthentication(this, error, null);
    }
  }

  async signIn(redirectUri: string | undefined) {
    if (redirectUri) {
      window.localStorage.setItem(REDIRECT_URI_STORE_KEY, redirectUri);
    } else {
      window.localStorage.removeItem(REDIRECT_URI_STORE_KEY);
    }

    const logError = this.auth.onLogError;

    return this.msalLibrary.loginRedirect(this.loginRequest).catch(function (error: any) {
      logError("signIn has thrown: " + error);
    });
  }

  async signOut() {
    this.auth.beforeSignOut(this);

    this.data.accessToken = "";
    this.data.idToken = "";
    this.data.user.name = "";
    this.data.user.email = "";
    this.data.account = null;
    this.data.roles = [];

    const logoutRequest = {
      account: this.msalLibrary.getAccountByUsername(this.data.user.email)
    };

    return this.msalLibrary.logout(logoutRequest);
  }

  async acquireToken(request: Request = this.tokenRequest, retries: number = 0, forceRefresh: boolean = false) {
    this.loginRequest.account = this.data.account || undefined;
    console.log("[MSAL][acquireToken] retries: " + retries);

    try {
      const r = { ...request, forceRefresh };
      const response = await this.msalLibrary.acquireTokenSilent(r);
      this.handleTokenResponse(null, response);
    } catch (error) {
      this.auth.onLogError("Failed to acquire token silently: " + error);
      this.auth.onToken(this, error as AuthError, null);

      if (error instanceof msal.InteractionRequiredAuthError || error instanceof msal.BrowserAuthError) {
        console.log("[MSAL] acquiring token using redirect");

        return this.msalLibrary.acquireTokenRedirect(request).catch((error: any) => {
          this.auth.onToken(this, error, null);
          this.auth.onLogError("Failed to acquire token with redirect: " + error);
        });
      } else if (retries > 0) {
        return await new Promise((resolve) => {
          setTimeout(async () => {
            const res = await this.acquireToken(request, retries - 1);
            resolve(res);
          }, 5 * 1000);
        });
      }

      return false;
    }
  }

  async acquireTokenForce(retries: number = 3) {
    console.log("[MSAL][acquireTokenForce] retries: " + retries);

    return this.acquireToken(this.tokenRequest, retries, true);
  }

  isAuthenticated() {
    const allAccounts = this.msalLibrary.getAllAccounts();

    if (allAccounts === null || allAccounts.length === 0) {
      return false;
    } else {
      return true;
    }
  }

  private selectAccount() {
    // Need to call getAccount here?
    const currentAccounts = this.msalLibrary.getAllAccounts();
    console.log("[MSAL][selectAccount] currentAccounts: ", currentAccounts);

    if (currentAccounts === null || currentAccounts.length === 0) {
      return;
    }

    // TODO: Implement more elaborate account selection logic?
    const account = currentAccounts[0];
    this.data.user.name = account.name || "";
    this.data.user.email = account.username || "";
    this.data.account = account;

    this.msalLibrary.setActiveAccount(account);
  }

  private handleTokenResponse(error: any, response: any) {
    monitorExpiresOn = new Date(0);

    if (error) {
      return;
    }

    if (this.data.accessToken !== response.accessToken) {
      this.data.roles = (response.idTokenClaims as any)["roles"] || [];

      monitorExpiresOn = response.expiresOn;
      this.setToken("accessToken", response.accessToken, response.expiresOn, response.scopes);

      // DK: Workaround for missing 'roles' field in idTokenClaims
      try {
        const at: any = jwtDecode(this.data.accessToken);

        if (at.roles) {
          this.data.roles.push(...at.roles);
        }
      } catch {
        console.error("[MSAL] Failed to parse Access Token", this.data.accessToken);
        this.auth.onLogError("[MSAL][handleTokenResponse] Failed to parse Access Token");
      }
    }

    if (this.data.idToken !== response.idToken) {
      this.setToken("idToken", response.idToken, new Date(response.idTokenClaims.exp * 1000), this.tokenRequest.scopes);
    }

    this.auth.onToken(this, null, response);
  }

  private setToken(tokenType: string, token: string, expiresOn: Date, scopes: string[]) {
    const expirationOffset = 300000; // 5 minutes layover
    const expiration = expiresOn.getTime() - new Date().getTime() - expirationOffset;

    if (expiration >= 0) {
      (this.data as any)[tokenType] = token;
    }

    if (this.tokenExpirationTimers[tokenType]) {
      clearTimeout(this.tokenExpirationTimers[tokenType]);
    }

    this.tokenExpirationTimers[tokenType] = window.setTimeout(async () => {
      console.log("[MSAL][setToken] auto refreshing token: " + this.auth.autoRefreshToken);

      if (this.auth.autoRefreshToken) {
        await this.acquireToken({ scopes }, 3, true);
        console.info(
          `[MSAL][setToken] Token has been refreshed. Old expiration: ${expiresOn.toISOString()}, new expiration: ${monitorExpiresOn.toISOString()}`
        );
      } else {
        (this.data as any)[tokenType] = "";
        console.log("[MSAL][setToken] setting token to none:" + this.data.accessToken);
      }
    }, expiration);
  }
}
